import { useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { useCountUp } from "../hooks/useCountUp";
import { MagneticButton } from "./MagneticButton";
import { TiltCard } from "./TiltCard";
import { AnimatedDP3D } from "./AnimatedDP3D";
import "./Hero.css";

const roles = ["Lead Engineer", "Full Stack Developer", "Cloud Architect", "Java & React Expert", "GenAI Builder"];

// Expo.out easing — premium feel per UI Pro Max Modern Dark spec
const EASE = [0.16, 1, 0.3, 1];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};

function HeroCard() {
  return (
    <TiltCard className="hero__card" maxRotation={15}>
      <div className="hero__card-avatar">
        <AnimatedDP3D variant="hero" letters="D" />
      </div>
      <div className="hero__card-name">Deepak Padmanabhan</div>
      <div className="hero__card-title">Lead Engineer · Westpac</div>
      <div className="hero__card-status">
        <div className="hero__card-status-item">
          <span className="hero__card-dot hero__card-dot--green" />
          Available for opportunities
        </div>
        <div className="hero__card-status-item">
          <span className="hero__card-dot hero__card-dot--purple" />
          Sydney, Australia 🇦🇺
        </div>
      </div>
      <div className="hero__card-tags">
        {["Java", "AWS", "React", "GenAI"].map((tag) => (
          <span key={tag} className="hero__card-tag">{tag}</span>
        ))}
      </div>
    </TiltCard>
  );
}

export function Hero() {
  const typingRef = useRef(null);
  const roleIndex = useRef(0);
  const charIndex = useRef(0);
  const isDeleting = useRef(false);
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true });

  useEffect(() => {
    let timeout;
    const type = () => {
      const current = roles[roleIndex.current];
      if (isDeleting.current) { charIndex.current--; }
      else { charIndex.current++; }
      if (typingRef.current) {
        typingRef.current.textContent = current.substring(0, charIndex.current);
      }
      let delay = isDeleting.current ? 50 : 90;
      if (!isDeleting.current && charIndex.current === current.length) {
        delay = 2200; isDeleting.current = true;
      } else if (isDeleting.current && charIndex.current === 0) {
        isDeleting.current = false;
        roleIndex.current = (roleIndex.current + 1) % roles.length;
        delay = 400;
      }
      timeout = setTimeout(type, delay);
    };
    timeout = setTimeout(type, 900);
    return () => clearTimeout(timeout);
  }, []);

  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  const statValues = ["7+", "2", "1.5M+", "PR"];
  const statLabels = ["Years Exp.", "Companies", "Txns/Day", "Australia"];

  const count0 = useCountUp(statValues[0], statsInView);
  const count1 = useCountUp(statValues[1], statsInView);
  const count2 = useCountUp(statValues[2], statsInView);
  const count3 = useCountUp(statValues[3], statsInView);
  const counts = [count0, count1, count2, count3];

  return (
    <section className="hero" id="home">
      {/* Ambient background */}
      <div className="hero__bg">
        <div className="hero__orb hero__orb--1" />
        <div className="hero__orb hero__orb--2" />
        <div className="hero__orb hero__orb--3" />
        <div className="hero__grid" />
        <div className="hero__noise" />
      </div>

      <div className="hero__content section-container">
        {/* Text side — staggered Framer Motion entrance */}
        <motion.div
          className="hero__text"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div className="hero__greeting" variants={itemVariants}>
            <span className="hero__greeting-line" />
            <span>Hello, I'm</span>
          </motion.div>

          <motion.h1 className="hero__name" variants={itemVariants}>
            Deepak<br />
            <span className="gradient-text">Padmanabhan</span>
          </motion.h1>

          <motion.div className="hero__role" variants={itemVariants}>
            <span ref={typingRef} className="hero__role-text" />
            <span className="hero__cursor">|</span>
          </motion.div>

          <motion.p className="hero__desc" variants={itemVariants}>
            7+ years crafting enterprise-grade applications at Westpac Group.
            Specialising in Java Spring Boot, React, AWS cloud-native architecture &amp; GenAI.
          </motion.p>

          <motion.div className="hero__actions" variants={itemVariants}>
            <MagneticButton
              className="hero__btn hero__btn--primary"
              onClick={() => scrollTo("#projects")}
            >
              View My Work
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </MagneticButton>
            <MagneticButton
              className="hero__btn hero__btn--glass"
              onClick={() => scrollTo("#contact")}
            >
              Get In Touch
            </MagneticButton>
          </motion.div>

          <motion.div className="hero__stats" variants={itemVariants} ref={statsRef}>
            {statLabels.map((label, i) => (
              <div key={label} style={{ display: "contents" }}>
                {i > 0 && <div className="hero__stat-divider" />}
                <div className="hero__stat">
                  <span className="hero__stat-value">{counts[i]}</span>
                  <span className="hero__stat-label">{label}</span>
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Visual side — 3D profile card */}
        <motion.div
          className="hero__visual"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: EASE, delay: 0.4 }}
        >
          <HeroCard />
        </motion.div>
      </div>

      <button className="hero__scroll" onClick={() => scrollTo("#about")}>
        <span>Scroll down</span>
        <div className="hero__scroll-icon">
          <div className="hero__scroll-dot" />
        </div>
      </button>
    </section>
  );
}
